import * as React from 'react'

import {
  InlineEditTextField,
  type InlineEditTextFieldStatus,
} from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { type TermOrMethodEditableField } from '@webapp/modules/purchasing/features/edit-term-or-method/models'
import messages from './messages'

export interface IEditableTermOrMethodTextFieldProps {
  value: string
  /** Which item property this cell edits — one View, rendered twice per row */
  field: TermOrMethodEditableField
  status: InlineEditTextFieldStatus
  onChange: (value: string) => void
  /** Commit the current value (fired on blur and Enter) */
  onCommit: () => void
  /** Revert to the persisted value (fired on Escape) */
  onEscape: () => void
}

export const EditableTermOrMethodTextField = ({
  value,
  field,
  status,
  onChange,
  onCommit,
  onEscape,
}: IEditableTermOrMethodTextFieldProps) => {
  const intl = useProcurifyIntl()

  const fieldMessages =
    field === 'name'
      ? {
          placeholder: messages.namePlaceholder,
          ariaLabel: messages.nameAriaLabel,
          idle: messages.nameStatusIconIdleAriaLabel,
          pending: messages.nameStatusIconPendingAriaLabel,
          resolved: messages.nameStatusIconResolvedAriaLabel,
          rejected: messages.nameStatusIconRejectedAriaLabel,
        }
      : {
          placeholder: messages.descriptionPlaceholder,
          ariaLabel: messages.descriptionAriaLabel,
          idle: messages.descriptionStatusIconIdleAriaLabel,
          pending: messages.descriptionStatusIconPendingAriaLabel,
          resolved: messages.descriptionStatusIconResolvedAriaLabel,
          rejected: messages.descriptionStatusIconRejectedAriaLabel,
        }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onCommit()
    } else if (e.key === 'Escape') {
      onEscape()
    }
  }

  return (
    <InlineEditTextField
      value={value}
      status={status}
      onChange={onChange}
      onBlur={onCommit}
      onKeyDown={handleKeyDown}
      placeholder={intl.formatMessage(fieldMessages.placeholder)}
      ariaLabel={intl.formatMessage(fieldMessages.ariaLabel)}
      statusAriaLabels={{
        idle: intl.formatMessage(fieldMessages.idle),
        pending: intl.formatMessage(fieldMessages.pending),
        resolved: intl.formatMessage(fieldMessages.resolved),
        rejected: intl.formatMessage(fieldMessages.rejected),
      }}
    />
  )
}

EditableTermOrMethodTextField.displayName = 'EditableTermOrMethodTextField'
